import { observable, action } from 'mobx';
import { ajax } from '@clouderwork/ywk';
import { withRole } from '@src/utils/residentNew';
import {
  EntryOfferParams,
  DropParams,
  ConfirmOfferParams,
  InterviewReviewParams,
  InterviewInviteParams,
  IInviteTplItem
} from '@src/entity/zhaopin/candidate';
import { IPosition, IAddress } from '@src/entity/zhaopin/position';
import { IMembersSel } from '@src/entity/company/project';

/**
 * @module:  候选人相关
 * @author: Liang Huang
 * @date: 2020-04-10 09:38:27
 */
class Candidate {
  @observable
  fetchState = 'init';
  // 发布职位字段
  @observable
  positionData: IAddress = {
    location: [0, 0], // 地点id
    address: '', //详细地址
    locationKey: ''
  };
  @observable
  managers: IMembersSel[] = [];
  @observable
  inviteTpls: IInviteTplItem[] = [];
  @observable
  cmFetching: string = 'init';
  /**
   * 获取公司成员 用于面试和接待人信息
   */
  @action.bound
  async getCompanyManagers() {
    this.cmFetching = 'pending';
    const res = await ajax.getJson('/zhaopin/api/company-managers', withRole({ status: 1 }));
    this.cmFetching = 'done';
    if (+res.error_code === 0) {
      this.cmFetching = 'success';
      this.managers = res.managers.map(({ user_uuid, name, email, phone, avatar }) => ({
        uuid: user_uuid,
        avatar: avatar,
        type: 1,
        name,
        email,
        phone
      }));
      return res.managers;
    } else {
      this.cmFetching = 'error';
    }
  }

  /**
   * 面试邀请模版
   */
  @action.bound
  async getInvitesTpl() {
    this.fetchState = 'pending';
    const res = await ajax.getJson('/zhaopin/api/company/invites', withRole({}));
    this.fetchState = 'done';
    if (+res.error_code === 0) {
      this.fetchState = 'success';
      this.inviteTpls = res.invites;
      return res.invites;
    } else {
      this.fetchState = 'error';
    }
  }
  /**
   * 邀请笔试
   */
  @action.bound
  async inviteExam(deliveryUuid: string) {
    this.fetchState = 'pending';
    const res = await ajax.post('/zhaopin/api/delivery/exam-invite/add', withRole({ delivery_uuid: deliveryUuid }));
    this.fetchState = 'done';
    if (+res.error_code === 0) {
      this.fetchState = 'success';
      return res.result;
    } else {
      this.fetchState = 'error';
    }
  }
  /**
   * 淘汰候选人
   */
  @action.bound
  async drop(params: DropParams) {
    this.fetchState = 'pending';
    const res = await ajax.postJson('/zhaopin/api/delivery/drop-resumes', withRole(params));
    this.fetchState = 'done';
    if (+res.error_code === 0) {
      this.fetchState = 'success';
      return res.result;
    } else {
      this.fetchState = 'error';
    }
  }
  /**
   * 录入offer
   */
  @action.bound
  async inputOffer(params: EntryOfferParams, entryId: number = 0) {
    const { delivery_uuid } = params;
    this.fetchState = 'pending';
    let func, url;
    if (entryId) {
      func = ajax.putJson;
      url = `/zhaopin/api/delivery/${delivery_uuid}/interview-entry/${entryId}`;
    } else {
      url = `/zhaopin/api/delivery/${delivery_uuid}/interview-entry`;
      func = ajax.postJson;
    }
    const res = await func(url, withRole(params));
    this.fetchState = 'done';
    if (+res.error_code === 0) {
      this.fetchState = 'success';
      return entryId ? res.entry : res.result;
    } else {
      this.fetchState = 'error';
    }
  }
  /**
   * 确认入职
   */
  @action.bound
  async confirmOffer(params: ConfirmOfferParams) {
    const { delivery_uuid, entry_id } = params;
    this.fetchState = 'pending';
    const res = await ajax.putJson(
      `/zhaopin/api/delivery/${delivery_uuid}/interview-entry/${entry_id}`,
      withRole(params)
    );
    this.fetchState = 'done';
    if (+res.error_code === 0) {
      this.fetchState = 'success';
      return res.entry;
    } else {
      this.fetchState = 'error';
    }
  }
  /**
   * 面试反馈
   */
  @action.bound
  async interviewReview(params: InterviewReviewParams) {
    const { delivery_uuid } = params;
    this.fetchState = 'pending';
    const res = await ajax.postJson(`/zhaopin/api/delivery/${delivery_uuid}/interview-result`, withRole(params));
    this.fetchState = 'done';
    if (+res.error_code === 0) {
      this.fetchState = 'success';
      return res.result;
    } else {
      this.fetchState = 'error';
    }
  }
  /**
   * 历史面试记录
   */
  @action.bound
  async getInterviewHisotry(delivery_uuid: string) {
    this.fetchState = 'pending';
    const res = await ajax.getJson(
      `/zhaopin/api/delivery/${delivery_uuid}/interview-result`,
      withRole({ delivery_uuid })
    );
    this.fetchState = 'done';
    if (+res.error_code === 0) {
      this.fetchState = 'success';
      return res.result;
    } else {
      this.fetchState = 'error';
    }
  }
  /**
   * 邀约面试
   */
  @action.bound
  async interviewInvite(params: InterviewInviteParams) {
    const { delivery_uuid } = params;
    this.fetchState = 'pending';
    const res = await ajax.postJson(`/zhaopin/api/delivery/${delivery_uuid}/interview-invite`, withRole(params));
    this.fetchState = 'done';
    if (+res.error_code === 0) {
      this.fetchState = 'success';
      return res.result;
    } else {
      this.fetchState = 'error';
    }
  }
  /**
   * 录用状态修改
   */
  @action.bound
  async waitEntry(delivery_uuid: string) {
    this.fetchState = 'pending';
    const res = await ajax.putJson(`/zhaopin/api/delivery/${delivery_uuid}`, withRole({ status: 600 }));
    this.fetchState = 'done';
    if (+res.error_code === 0) {
      this.fetchState = 'success';
      return res.delivery;
    } else {
      this.fetchState = 'error';
    }
  }
  /**
   * 获取笔试信息列表
   */
  @action.bound
  async getExamInfo(id: number) {
    this.fetchState = 'pending';
    const res = await ajax.post(`/zhaopin/api/delivery/exam-invite`, withRole({ delivery_id: id }));
    this.fetchState = 'done';
    if (+res.error_code === 0) {
      this.fetchState = 'success';
      return res.result[0];
    } else {
      this.fetchState = 'error';
    }
  }
  /**
   * 获取offer信息列表
   */
  @action.bound
  async getOfferInfo(id: string, params = {}) {
    this.fetchState = 'pending';
    const res = await ajax.getJson(`/zhaopin/api/delivery/${id}/interview-entry`, withRole(params));
    this.fetchState = 'done';
    if (+res.error_code === 0) {
      this.fetchState = 'success';
      return res.entry;
    } else {
      this.fetchState = 'error';
    }
  }
  /**
   * 获取联系方式
   */
  @action.bound
  async getContact(id: string) {
    this.fetchState = 'pending';
    const res = await ajax.post(`/zhaopin/api/recommend/contact`, withRole({ recommend: id }));
    this.fetchState = 'done';
    if (+res.error_code === 0) {
      this.fetchState = 'success';
      return res;
    } else {
      this.fetchState = 'error';
    }
  }

  // 查重
  @action.bound
  async checkRepeat(delivery: string, phone: string) {
    return await ajax.post('/zhaopin/api/delivery/check/phone', withRole({ delivery, phone }));
  }
  // 确权
  @action.bound
  async checkAuth(delivery: string) {
    return await ajax.post('/zhaopin/api/delivery/auth', withRole({ delivery }));
  }

  @action.bound
  async confirmPay(deliveryUuid: string, join_at: string) {
    this.fetchState = 'pending';
    const res = await ajax.post('/zhaopin/api/delivery/order', {
      delivery: deliveryUuid,
      join_at,
      role: 'company'
    });
    this.fetchState = 'done';
    return res;
  }

  /**
   * 获取职位的投递详情
   */
  @action.bound
  async getDeliveryDetail(deliveryUuid: string) {
    this.fetchState = 'pending';
    const res = await ajax.getJson(`/zhaopin/api/delivery/${deliveryUuid}`, withRole({}, 2));
    this.fetchState = 'done';
    return res;
  }
}
export default Candidate;
